import Vue from "vue";
import $axios from "@widget/plugins/axios";
import { v4 as uuidv4 } from "uuid";

const widgetData = {
    namespaced: true,
    state: () => ({
        selectedData: {
            audience: null,
            subAudience: null,
            selects: []
        },
        selectedDataLocalStorage: {
            audience: null,
            subAudience: null,
            selects: []
        },
        geography: "all",
        audienceData: [],
        userChanged: false,
        showRightbar: false,
        showfilter: false,
    }),
    mutations: {
        setAudience (state, payload) {
            Vue.set(state.selectedData, "audience", payload);
            Vue.set(state.selectedData, "selects", []);

            let activeAudience = state.audienceData.find(item => {
                return item.code === state.selectedData.audience;
            });

            if (activeAudience) {
                Vue.set(state.selectedData, "subAudience", activeAudience.children[0].code);
            }
        },
        setSubAudience (state, payload) {
            Vue.set(state.selectedData, "subAudience", payload);
        },
        setAudienceData (state, payload) {
            let audienceData = payload.map(item => ({
                code: item.list_code,
                title: item.title,
                description: item.description,
                key: uuidv4(),
                children: item.presets.map(_preset => ({
                    code: _preset.code,
                    title: _preset.title,
                    description: _preset.description,
                    key: uuidv4()
                })),
                groups: item.selection_groups,
                selections: item.selections.map(select => ({
                    key: uuidv4(),
                    code: select.code,
                    title: select.title,
                    description: select.description,
                    attentionText: select.attentionText,
                    typeSelection: select.type,
                    type: select.widget,
                    group: select.group,
                    config: select.widget_config,
                    values: select.values ? select.values.map(value => ({
                        key: uuidv4(),
                        ...value
                    })) : [],
                    valuesGrouped: select.valuesGrouped ? select.valuesGrouped.map(value => ({
                        key: uuidv4(),
                        ...value
                    })) : []
                }))
            }));

            let b2bItem = audienceData.find(item => item.code === window.widget_config.b2bBase);
            let b2cItem = audienceData.find(item => item.code === window.widget_config.b2cBase);

            if (window.widget_config.audienceDefault === window.widget_config.b2bBase) {
                state.audienceData = [b2bItem, b2cItem];
            } else {
                state.audienceData = [b2cItem, b2bItem];
            }

            let defaultAudience = state.audienceData[0];
            if (window.widget_config.audienceDefault) {
                defaultAudience = state.audienceData.find(item => item.code === window.widget_config.audienceDefault);
            }

            if (defaultAudience) {
                Vue.set(state.selectedData, "audience", defaultAudience.code);
                Vue.set(state.selectedData, "subAudience", defaultAudience.children[0].code);
            }
        },
        setGeography (state, payload) {
            state.geography = payload || "all";
        },
        addSelect (state, payload) {
            let indexSelect = state.selectedData.selects.findIndex(item => item.code === payload.code);
            if (indexSelect !== -1) {
                Vue.set(state.selectedData.selects, indexSelect, payload);
            } else {
                state.selectedData.selects.push(payload);
            }

            if (!payload.silentMode) {
                state.userChanged = true;
            }
        },
        addSelectGeo (state, payload) {
            state.selectedData.selects.push(payload);
            if (!payload.silentMode) {
                state.userChanged = true;
            }
        },
        removeSelect (state, payload) {
            let indexSelect = state.selectedData.selects.findIndex(item => item.code === payload);
            if (indexSelect !== -1) {
                Vue.delete(state.selectedData.selects, indexSelect);
            }
        },
        removeSelectGeo (state, payload) {
            let indexSelect = state.selectedData.selects.findIndex(item => item.code === payload.code && item.query === payload.query);
            if (indexSelect !== -1) {
                Vue.delete(state.selectedData.selects, indexSelect);
            }
        },
        clearAll (state) {
            let defaultAudience = state.audienceData[0];

            if (defaultAudience) {
                Vue.set(state.selectedData, "audience", defaultAudience.code);
                Vue.set(state.selectedData, "subAudience", defaultAudience.children[0].code);
                Vue.set(state.selectedData, "selects", []);
            }
        },
        loadSelectFromLocalstorage (state, payload = state.selectedDataLocalStorage) {
            state.selectedData = payload;
        },
        loadLocalstorage (state, payload) {
            state.selectedDataLocalStorage = payload;
        },
        setUserChanged (state) {
            state.userChanged = true;
        },
        setValuesSelect (state, payload) {
            let activeAudience = state.audienceData.find(item => {
                return item.code === state.selectedData.audience;
            });

            if (activeAudience) {
                let findSelection = activeAudience.selections.find(item => {
                    return item.code === payload.code;
                });

                if (findSelection) {
                    Vue.set(findSelection, payload.key, payload.values);
                }
            }
        }
    },
    getters: {
        selectedData: state => {
            return state.selectedData;
        },
        getGeography: state => {
            return state.geography;
        },
        getAudienceData: state => {
            return state.audienceData;
        },
        getAudienceDataByCode: state => code => {
            return state.audienceData.find(item => item.code === code);
        },
        getAudienceChildren: state => {
            let activeAudience = state.audienceData.find(item => {
                return item.code === state.selectedData.audience;
            });

            if (activeAudience) {
                return activeAudience.children;
            }
        },
        getXPerSelection: state => {
            let activeAudience = state.audienceData.find(item => {
                return item.code === state.selectedData.audience;
            });

            if (activeAudience) {
                return activeAudience.selections.find(item => item.typeSelection === "OMPDIST");
            }
        },
        getAudienceSelections: state => type => {
            let activeAudience = state.audienceData.find(item => {
                return item.code === state.selectedData.audience;
            });

            if (activeAudience) {
                return activeAudience.selections.filter(item => item.typeSelection === type);
            }
        },
        getSelectionsByCode: state => (code, grouped = false) => {
            let activeAudience = state.audienceData.find(item => {
                return item.code === state.selectedData.audience;
            });

            if (activeAudience) {
                let selection = activeAudience.selections.find(item => item.code === code);
                if (selection) {
                    return grouped ? selection.valuesGrouped : selection.values;
                }
                return [];
            }
            return [];
        },
        getSelectedDataForUI: state => {
            let audience = state.audienceData.find(item => item.code === state.selectedData.audience);
            let subAudience = audience.children.find(item => item.code === state.selectedData.subAudience);

            return {
                audience: {
                    code: audience.code,
                    title: audience.title
                },
                subAudience: {
                    code: subAudience.code,
                    title: subAudience.title
                },
                selects: state.selectedData.selects.map(item => ({
                    key: uuidv4(),
                    ...item
                }))
            };
        },
        getSelectedDataLocalStorageForUI: state => {
            let audience = state.audienceData.find(item => item.code === state.selectedDataLocalStorage.audience);
            let subAudience = audience.children.find(item => item.code === state.selectedDataLocalStorage.subAudience);

            return {
                audience: {
                    code: audience.code,
                    title: audience.title
                },
                subAudience: {
                    code: subAudience.code,
                    title: subAudience.title
                },
                selects: state.selectedDataLocalStorage.selects.map(item => ({
                    key: uuidv4(),
                    ...item
                }))
            };
        },
        getSelectByCode: state => code => {
            return state.selectedData.selects.find(item => item.code === code);
        },
        getQueryForAPI: state => {
            let selections = state.selectedData.selects.filter(item => {
                return !item.code.includes("_geo_search") && !item.code.includes("_zip_radius") && !item.code.includes("_mapping");
            }).map(item => {
                let reformatQuery = [];
                if (Array.isArray(item.query)) {
                    item.query.forEach(item => {
                        if (typeof item === "string") {
                            reformatQuery.push(...item.split(","));
                        } else {
                            reformatQuery.push(item);
                        }
                    });
                }

                let query = typeof item.query === "string" ? [item.query] : reformatQuery;

                return [
                    item.code,
                    query
                ];
            });

            // selectionsGeoSearch
            let selectionsGeoSearch = state.selectedData.selects.filter(item => {
                return item.code.includes("_geo_search");
            });

            if (selectionsGeoSearch.length) {
                let array = [
                    `${state.selectedData.audience}_geo_search`
                ];

                let arrayValues = [];
                if (selectionsGeoSearch.filter(item => item.type === "zip").length) {
                    arrayValues.push(["zip", selectionsGeoSearch.filter(item => item.type === "zip").map(item => item.query)]);
                }

                if (selectionsGeoSearch.filter(item => item.type === "state").length) {
                    arrayValues.push(["state", selectionsGeoSearch.filter(item => item.type === "state").map(item => item.query)]);
                }

                if (selectionsGeoSearch.filter(item => item.type === "county").length) {
                    arrayValues.push(["county", selectionsGeoSearch.filter(item => item.type === "county").map(item => item.query)]);
                }

                if (selectionsGeoSearch.filter(item => item.type === "city").length) {
                    arrayValues.push(["city", selectionsGeoSearch.filter(item => item.type === "city").map(item => item.query)]);
                }

                if (arrayValues.length) {
                    array.push(arrayValues);
                }

                if (array.length > 1) {
                    selections.push(array);
                }
            }
            // end

            // selectionsGeoZipRadius
            let selectionsGeoZipRadius = state.selectedData.selects.filter(item => {
                return item.code.includes("_zip_radius");
            });

            if (selectionsGeoZipRadius.length) {
                let array = [
                    `${state.selectedData.audience}_zip_radius`,
                    selectionsGeoZipRadius.map(item => `${item.query}:${item.radius}`)
                ];

                if (array.length > 1) {
                    selections.push(array);
                }
            }
            // end

            // selectionsGeoMapping
            let selectionsGeoMapping = state.selectedData.selects.filter(item => {
                return item.code.includes("_mapping");
            });

            if (selectionsGeoMapping.length) {
                let array = [
                    `${state.selectedData.audience}_mapping`,
                    selectionsGeoMapping.map(item => item.query)
                ];

                if (array.length > 1) {
                    selections.push(array);
                }
            }
            // end

            return {
                preset_code: state.selectedData.subAudience,
                selections
            };
        },
        getUserChanged: state => {
            return state.userChanged;
        },
        getDescriptionData: (state, getters) => {
            let selectedData = getters["getSelectedDataForUI"];
            let items = selectedData.selects.map(item => ({
                title: item.title,
                sensitive: item.sensitive
            }));
            if (!selectedData.selects.find(item => item.code.includes("_geo_search") || item.code.includes("_zip_radius") || item.code.includes("_mapping"))) {
                items.unshift({
                    title: "Whole USA",
                    sensitive: false
                });
            }
            if (selectedData.subAudience.code.includes("_email")) {
                items.unshift({
                    title: "Must have email",
                    sensitive: false
                });
            }
            return items;
        },
        getSelectionGroups: state => {
            let activeAudience = state.audienceData.find(item => {
                return item.code === state.selectedData.audience;
            });

            if (activeAudience) {
                return activeAudience.groups;
            }
        },
        getGroupBySelect: state => select => {

        }
    },
    actions: {
        getConfig ({ commit }) {
            let url = "/api/widget/config";
            let paramsString = window.location.search.substr(1);
            if (paramsString) {
                url += `?${paramsString}`;
            }

            return $axios.get(url).then(({ data: response }) => {
                commit("setAudienceData", response);
            });
        },
        getValues ({}, payload) {
            return $axios.get(`/api/values/${payload.code}`, {
                params: {
                    query: payload.query
                }
            });
        },
        saveSelectToLocalstorage ({ state }) {
            if (!state.selectedData.selects.length) {
                return;
            }

            let date = new Date();
            let expireDate = date.setDate(date.getDate() + 10);
            let storeForLocalstorage = {
                expireDate: expireDate,
                data: state.selectedData
            };
            localStorage.setItem("widget-store", JSON.stringify(storeForLocalstorage));
        },
        checkout ({ getters, rootGetters }) {
            return $axios.post("/api/cart", {
                ...getters["getQueryForAPI"],
                quantity: rootGetters["calculateLeads/getQuantity"],
                payment_way: rootGetters["calculateLeads/getPaymentWay"],
                description_data: getters["getDescriptionData"]
            });
        },
        saveList ({ state, getters, rootGetters }, payload) {
            let url = "/api/cart/count";
            if (window.widget_config.logged) {
                url = "/api/count/create-by-params";
            }

            return $axios.post(url, {
                ...getters["getQueryForAPI"],
                quantity: rootGetters["calculateLeads/getQuantity"],
                payment_way: rootGetters["calculateLeads/getPaymentWay"],
                title: payload,
                presets: state.selectedData,
                description_data: getters["getDescriptionData"]
            });
        },
        shareList ({ state, getters }) {
            return $axios.post("/api/shared-count", {
                ...getters["getQueryForAPI"],
                preset_code: state.selectedData.subAudience,
                presets: state.selectedData
            });
        },
        getTree ({ commit }, payload) {
            let url = `/api/values/${payload.code}`;
            let paramsString = window.location.search.substr(1);
            if (paramsString) {
                url += `?${paramsString}`;
            }

            return $axios.get(url, {
                params: payload.params
            }).then(({ data: { data: response } }) => {
                commit("setValuesSelect", {
                    code: payload.code,
                    key: payload.params.grouped ? "valuesGrouped" : "values",
                    values: response
                });
            });
        },
        getTreeOneLevel ({ commit }, payload) {
            let url = `/api/values/${payload.code}`;
            let paramsString = window.location.search.substr(1);
            if (paramsString) {
                url += `?${paramsString}`;
            }

            return $axios.get(url, {
                params: payload.params
            });
        }
    }
};

export default widgetData;
